<template>
  <div class="add-address">
    <div class="form-row">
    <component :is="style">
      .google-search-input{
        border :2px solid {{myTheme.customizeButton.backgroundColor}} ;
        padding: 10px 18px 10px 18px;
      }
      .bolder{
        font-weight : bold;
      }
    </component>
      <!-- Postcode Lookup -->
      <div class="form-group col-md-12">
        <label class="bolder">{{$t('input.placeholder.search_postCode')}}</label>
        <vue-google-autocomplete
        autocomplete="false"
          :id="lookupId"
          ref="lookup"
          class="form-control google-search-input"
          v-on:placechanged="lookupAddressFromPostcode"
          :placeholder="placeholderText"
          types="(regions)"
          :country="getIsUKApp? 'uk':'es'"
        />
      </div>
    </div>

    <div class="form-row" v-if="isAuthenticated">
      <!-- Line 2 -->
      <div class="form-group col-md-12">
        <label>{{$t('label.name_for_address')}}</label>
        <fg-input type="text" v-model="localAddress.name" :disabled="disabled" />
      </div>
    </div>

    <div class="form-row">
      <!-- Street Number -->
      <div class="form-group col-md-4">
        <label>{{$t('label.street_number')}} <span class="required">*</span></label>
        <fg-input
          type="text" :disabled="disabled" 
          v-model="localAddress.street_number"
          :class="{
            'has-danger': errors.streetNumber === true && !disabled,
            'has-success': errors.streetNumber === false,
          }"
        />
        <div class="required-small input-group" v-if="errors.streetNumber === true && !disabled">
          {{$t('validation.is_required', {resource_name : 'Street number'})}}
        </div>
      </div>

      <!-- Line 1 -->
      <div class="form-group col-md-8">
        <label>{{$t('label.line_1')}} <span class="required">*</span></label>
        <fg-input type="text" v-model="localAddress.address_line_1" :disabled="disabled" 
                  :class="{
            'has-danger': errors.street && !disabled,
            'has-success': errors.street === false,
          }"
        />
        <div class="required-small input-group" v-if="errors.street === true && !disabled">
          {{ $t('validation.provide_resource', {resource_name : 'complete address'}) }}
        </div>
      </div>
    </div>

    <div class="form-row">
      <!-- Line 2 -->
      <div class="form-group col-md-12">
        <label>{{ $t('label.line_2') }}</label>
        <fg-input type="text" :disabled="disabled"  v-model="localAddress.address_line_2" />
      </div>
    </div>

    <div class="form-row">
      <!-- City -->
      <div class="form-group col-md-6">
        <label>{{$t('label.city')}} <span class="required">*</span></label>
        <fg-input type="text" v-model="localAddress.city" :disabled="disabled"           
        :class="{
            'has-danger': errors.city === true && !disabled,
            'has-success': errors.city === false,
          }"
        />
        <div class="required-small input-group" v-if="errors.city === true && !disabled">
          {{ $t('validation.provide_resource', {resource_name : 'city'}) }}
        </div>
      </div>

      <!-- Region -->
      <div class="form-group col-md-6">
        <label>{{$t('label.region')}} <span class="required">*</span></label>
        <fg-input :disabled="disabled" 
        :class="{
            'has-danger': errors.region === true && !disabled,
            'has-success': errors.region === false,
          }"
         type="text" v-model="localAddress.region" />
        <div class="required-small input-group" v-if="errors.region === true && !disabled">
          {{ $t('validation.provide_resource', {resource_name : 'region'}) }}
        </div>
      </div>
    </div>

    <div class="form-row">
      <!-- Country -->
      <div class="form-group col-md-6">
        <label>{{$t('label.country')}} <span class="required">*</span></label>
        <fg-input :disabled="disabled" 
        :class="{
            'has-danger': errors.country === true && !disabled,
            'has-success': errors.country === false,
          }"
        type="text" v-model="localAddress.country" />
        <div class="required-small input-group" v-if="errors.country === true && !disabled">
          {{ $t('validation.provide_resource', {resource_name : 'country'}) }}
        </div>
      </div>

      <!-- Postcode -->
      <div class="form-group col-md-6">
        <label>Postcode <span class="required">*</span></label>
        <fg-input type="text" v-model="localAddress.postcode" :disabled="disabled" 
        :class="{
            'has-danger': errors.postcode === true && !disabled,
            'has-success': errors.postcode === false,
          }"
        />
        <div class="required-small input-group" v-if="errors.postcode === true && !disabled">
          {{ $t('validation.provide_resource', {resource_name : 'Postcode'}) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue2Filters from "vue2-filters";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import { FormGroupInput } from "@/components";
import { mapGetters } from "vuex";

export default {
  name: "address-lookup-form",
  mixins: [Vue2Filters.mixin],
  components: {
    [FormGroupInput.name]: FormGroupInput,
    VueGoogleAutocomplete,
  },
  props :{
    address: Object,
    isNewAddress : Boolean,
    placeholderText : {required: true, type:String}
  },
  watch: {
    isNewAddress: function(value){
      this.disabled = value;
    },
    lookupResult: function(value) {
      this.localAddress = {
        ...this.localAddress,
        ...value,
      };
    },
    localAddress: {
      deep: true,
      handler: function() {
        this.submitAddress();
        this.$refs.lookup.clear();
      },
    },
  },
  data: function() {
    return {
      style: "style",
      errors: {
        postcode: null,
        streetNumber: null,
        street: null,
        city: null,
        region:null,
        country:null
      },
      lookupResult: {},
      disabled:this.isNewAddress,
    };
  },
  computed: {
    ...mapGetters(["isAuthenticated", "getIsUKApp"]),
    lookupId: () => {
      return "postcode-lookup" + Math.floor(1000 + Math.random() * 9000);
    },
    localAddress: {
    // getter
      get: function () {
        return this.address;
      },
      // setter
      set: function (newValue) {
        this.address.address_line_1 = newValue.address_line_1;
        this.address.address_line_2 = newValue.address_line_2;
        this.address.city = newValue.city;
        this.address.region = newValue.region;
        this.address.country = newValue.country;
        this.address.id = newValue.id;
        this.address.is_primary = newValue.is_primary;
        this.address.lat = newValue.lat;
        this.address.lng = newValue.lng;
        this.address.name = newValue.name;
        this.address.notes = newValue.notes;
        this.address.postcode = newValue.postcode;
        this.address.street_number = newValue.street_number;
      }
    }
  },
  methods: {
    submitAddress() {     
      const formattedAddress = {
        postcode: this.address.postcode,
        street_number: this.address.street_number ? this.address.street_number.trim() : "",
        address_line_1: this.address.address_line_1 ? this.address.address_line_1.trim() : "",
        address_line_2: this.address.address_line_2,
        name: this.address.name,
        city: this.address.city,
        region: this.address.region,
        country: this.address.country,
        lat: this.lookupResult.lat,
        lng: this.lookupResult.lng,
      };

      //** check if the required fields are provided */
      if (this.checkAddressForm()) {
        this.$emit("updateAddress", formattedAddress);
      }
    },
    checkAddressForm() {
      this.errors.postcode = !this.address.postcode;
      this.errors.streetNumber = !this.address.street_number;
      this.errors.street = !this.address.address_line_1;
      this.errors.city = !this.address.city;
      this.errors.region = !this.address.region;
      this.errors.country = !this.address.country
      return !Object.keys(this.errors).some((k) => this.errors[k]); // check if any of the element generate an error
    },
    lookupAddressFromPostcode(addData, place) {
      
      let addressKeys = {
        street_number: "street_number",
        postal_code: "postcode",
        
      };
      if(this.getIsUKApp){
       addressKeys.postal_town = "city";
        addressKeys.administrative_area_level_1= "country";
        addressKeys.administrative_area_level_2 =  "region";
        addressKeys.route = "address_line_1";
      }else{
        addressKeys.locality = "address_line_1"
        addressKeys.administrative_area_level_2= "city";
        addressKeys.country = "country";
        addressKeys.administrative_area_level_1= "region";
      }
      //console.log(addressKeys)

      const lookupResult = Object.keys(addressKeys).reduce((set, key) => {
        const items = place.address_components.filter((x) =>
          x.types.includes(key)
        );
        return {
          ...set,
          [addressKeys[key]]: items.length ? items[0]["long_name"] : "",
        };
      }, {});

      this.lookupResult = {
        ...lookupResult,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      this.disabled = false;
    },
  }
};
</script>

<style scoped>
.form-group.input-group {
  padding: 0;
}
</style>
